import React, { useEffect, useReducer } from "react";
import {
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";

import { style } from "./style";

import Button from "@/components/Button";
import GetImages from "@/components/GetImages";
import FullScreenDialog from "@/components/FullScreenDialog";
import { InputField } from "@/components/InputField";
import CreateEditAvailabilityOverlays from "./Overlays";
import TimePicker from "@/components/TimePicker";
import SaveCancelModalFooterActions from "@/components/AvailabilitySettings/SaveCancelModalFooterActions";
import DatePickerMultipleSelection from "@/components/DatePickerMultipleSelection";

import { IButtonVariant } from "@/models/button";
import {
  IDoctorConsultationType,
  IProfileDropdownEnums,
  ITimePickerType,
} from "@/models/doctor";
import {
  IAvailabilitySettingPermissionEnums,
  IDoctorPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";
import { ISessionKeys } from "@/models";
import {
  ICreateEditAvailabilityAction,
  ICreateEditAvailabilityActionType,
  ICreateEditAvailabilityState,
  IStartAndEndTime,
} from "@/models/ApiRequestResponse/doctor";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setTopBarDropdownMultipleItems } from "@/store/slices/doctor/topNavigation";
import {
  availabilitySettings,
  fetchAvailabilitySettingsListing,
  fetchClinicList,
  setDayDateFilter,
  setIsDeleteAvailability,
  setIsEditAvailability,
  setSelectedAvailabilitySetting,
  setSelectedAvailabilitySettingDetail,
} from "@/store/slices/doctor/availabilitySettings";

import {
  calculateSlotDuration,
  getDateFormat,
  slotReserveValues,
  timeInAmPmFormat,
  topBarDropdownDispatchingEnums,
} from "@/utils";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";
import {
  createAvailabilitySettingApi,
  deleteAvailabilitySettingApi,
  updateAvailabilitySettingApi,
} from "@/utils/apis/doctor/availabilitySetting";
import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import { INotifyEnum, notify } from "@/utils/toaster";
import { availabilityInputsNames, consultationTypes } from "@/utils/common";
import { storage } from "@/utils/Storage";

const initialState: ICreateEditAvailabilityState = {
  consultationTypeOrClinicSelectionOpen: {
    consultationType: false,
    clinicName: false,
  },
  slotReserveFromOpen: false,
  startEndTimeSelectionOpen: false,
  selectedDates: [],
  formData: {
    consultationType: "",
    clinicName: "",
    clinicId: "",
    startTime: "",
    endTime: "",
    duration: "",
    consultationFee: "",
    bufferDuration: "",
    slotDuration: "",
    totalSlots: "",
    appointmentDisplay: "",
    reserveSlots: "",
    slotReserveFrom: "",
    startAndEndTime: [],
  },
  saveOrUpdateLoading: false,
};

const reducer = (
  state: ICreateEditAvailabilityState,
  action: ICreateEditAvailabilityActionType
): ICreateEditAvailabilityState => {
  switch (action.type) {
    case ICreateEditAvailabilityAction.SET_CONSULTATION_TYPE_OPEN:
      return {
        ...state,
        consultationTypeOrClinicSelectionOpen: {
          ...state.consultationTypeOrClinicSelectionOpen,
          consultationType: action.payload,
        },
      };
    case ICreateEditAvailabilityAction.SET_CLINIC_NAME_OPEN:
      return {
        ...state,
        consultationTypeOrClinicSelectionOpen: {
          ...state.consultationTypeOrClinicSelectionOpen,
          clinicName: action.payload,
        },
      };
    case ICreateEditAvailabilityAction.SET_START_END_TIME:
      return {
        ...state,
        startEndTimeSelectionOpen: action.payload,
      };
    case ICreateEditAvailabilityAction.SET_SLOT_RESERVE_FROM:
      return {
        ...state,
        slotReserveFromOpen: action.payload,
      };
    case ICreateEditAvailabilityAction.SET_SELECTED_DATES:
      return {
        ...state,
        selectedDates: action.payload,
      };
    case ICreateEditAvailabilityAction.SET_FORM_DATA:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
        },
      };
    case ICreateEditAvailabilityAction.SET_SAVE_OR_UPDATE_LOADING:
      return {
        ...state,
        saveOrUpdateLoading: action.payload,
      };
    default:
      return state;
  }
};

const CreateEditAvailability: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    consultationTypeOrClinicSelectionOpen,
    selectedDates,
    formData,
    slotReserveFromOpen,
    startEndTimeSelectionOpen,
    saveOrUpdateLoading,
  } = state;

  const dispatchApp = useAppDispatch();
  const {
    isDeleteAvailability,
    isEditAvailability,
    selectedAvailabilitySettingDetail,
    clinicList,
  } = useAppSelector(availabilitySettings);

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;

  const intl = useIntl();
  const permissionRestrictionHandler = usePermissionRestriction();

  const closeStartEndTimePickerHandler = () =>
    dispatch({
      type: ICreateEditAvailabilityAction.SET_START_END_TIME,
      payload: false,
    });

  const startEndSaveTimeHandler = (start_time: string, end_time: string) => {
    if (start_time && end_time) {
      const startAndEndTime = [
        ...formData.startAndEndTime,
        { start_time, end_time },
      ];

      if (end_time && startAndEndTime?.length > 2) {
        notify(
          INotifyEnum.ERROR,
          "start and end time must contain less than or equal to 2 items"
        );
        closeStartEndTimePickerHandler();
        return;
      }

      dispatch({
        type: ICreateEditAvailabilityAction.SET_FORM_DATA,
        payload: {
          startAndEndTime,
        },
      });
      closeStartEndTimePickerHandler();
    }
  };

  const removeStartEndTimeHandler = (startTime: string, endTime: string) => {
    const restTimeSlots = formData.startAndEndTime.filter(
      (time) => time.start_time !== startTime && time.end_time !== endTime
    );
    dispatch({
      type: ICreateEditAvailabilityAction.SET_FORM_DATA,
      payload: {
        startAndEndTime: restTimeSlots,
      },
    });
  };

  const closeAvailabilitySelectionModal = () => {
    dispatch({
      type: ICreateEditAvailabilityAction.SET_CONSULTATION_TYPE_OPEN,
      payload: false,
    });
    dispatch({
      type: ICreateEditAvailabilityAction.SET_CLINIC_NAME_OPEN,
      payload: false,
    });
  };

  const closeSlotReserveFromHandler = () =>
    dispatch({
      type: ICreateEditAvailabilityAction.SET_SLOT_RESERVE_FROM,
      payload: false,
    });

  const confirmDeleteOpenCloseHandler = () =>
    dispatchApp(setIsDeleteAvailability(!isDeleteAvailability));

  const deleteAvailabilitySetting = async () => {
    const deleteAvailabilityResp = await deleteAvailabilitySettingApi(
      selectedAvailabilitySettingDetail?.id!
    );
    try {
      if (deleteAvailabilityResp.statusCode === IAPIStatusCode.SUCCESS) {
        dispatchApp(fetchAvailabilitySettingsListing({}));
        dispatchApp(setSelectedAvailabilitySetting(null));
        dispatchApp(setSelectedAvailabilitySettingDetail(null));
        dispatchApp(setIsEditAvailability(false));
        dispatchApp(
          setTopBarDropdownMultipleItems([
            topBarDropdownDispatchingEnums[
              IProfileDropdownEnums.CREATE_EDIT_AVAILABILITY
            ],
            topBarDropdownDispatchingEnums[
              IProfileDropdownEnums.AVAILABILITY_SETTINGS
            ],
          ])
        );
        notify(INotifyEnum.SUCCESS, deleteAvailabilityResp.message);
      } else {
        notify(INotifyEnum.ERROR, deleteAvailabilityResp.message);
      }
      confirmDeleteOpenCloseHandler();
    } catch (error) {
      console.log("error", error);
    }
  };

  const deleteAvailabilityHandler = () => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.AVAILABILITY_SETTING,
      IAvailabilitySettingPermissionEnums.DELETE_AVAILABILITY
    );
    if (!isPermissionGranted) return;

    confirmDeleteOpenCloseHandler();
  };

  const backCreateEditAvailabilityHandler = () => {
    dispatchApp(
      setTopBarDropdownMultipleItems([
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.CREATE_EDIT_AVAILABILITY
        ],
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.AVAILABILITY_SETTINGS
        ],
      ])
    );
    dispatchApp(setDayDateFilter(null));
  };

  const closeCreateOrEditAvailabilityHandler = () => {
    dispatchApp(setSelectedAvailabilitySetting(null));
    dispatchApp(setSelectedAvailabilitySettingDetail(null));
    dispatchApp(setIsEditAvailability(false));
    dispatchApp(
      setTopBarDropdownMultipleItems([
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.CREATE_EDIT_AVAILABILITY
        ],
        topBarDropdownDispatchingEnums[
          IProfileDropdownEnums.AVAILABILITY_SETTINGS
        ],
      ])
    );
    dispatchApp(setDayDateFilter(null));
  };

  const selectTypeOfConsultationHandler = (value: string) => {
    dispatch({
      type: ICreateEditAvailabilityAction.SET_CONSULTATION_TYPE_OPEN,
      payload: false,
    });
    dispatch({
      type: ICreateEditAvailabilityAction.SET_FORM_DATA,
      payload: { consultationType: value },
    });
    if (value === IDoctorConsultationType.IN_CLINIC) {
      dispatchApp(fetchClinicList());
    } else {
      dispatch({
        type: ICreateEditAvailabilityAction.SET_FORM_DATA,
        payload: {
          consultationFee: storage.getSessionToken(
            ISessionKeys.CONSULTATION_FEE
          ),
        },
      });
    }
  };

  const handleDateSelection = (dates: string[]) => {
    dispatch({
      type: ICreateEditAvailabilityAction.SET_SELECTED_DATES,
      payload: dates,
    });
  };

  const saveClinicNameHandler = (value: string) => {
    const [_, consultationFee] = value.split("_");
    dispatch({
      type: ICreateEditAvailabilityAction.SET_CLINIC_NAME_OPEN,
      payload: false,
    });

    const clinicName = clinicList.find((item) => item.value === value)?.label;
    dispatch({
      type: ICreateEditAvailabilityAction.SET_FORM_DATA,
      payload: { clinicName, clinicId: value, consultationFee },
    });
  };

  const saveSlotReserveFromHandler = (value: string) => {
    dispatch({
      type: ICreateEditAvailabilityAction.SET_FORM_DATA,
      payload: {
        slotReserveFrom: value,
      },
    });
    closeSlotReserveFromHandler();
  };

  const createOrEditAvailabilitySettingsHandler = async () => {
    dispatch({
      type: ICreateEditAvailabilityAction.SET_SAVE_OR_UPDATE_LOADING,
      payload: true,
    });
    try {
      let createEditAvailability;
      const start_and_end_time =
        formData.consultationType === IDoctorConsultationType.IN_CLINIC
          ? formData.startAndEndTime
          : [
              {
                start_time: formData.startTime,
                end_time: formData.endTime,
              },
            ];

      const payload = {
        ...(!isEditAvailability && {
          type_of_consultation: formData.consultationType,
          consultation_fee: +formData.consultationFee,
          schedule_dates: selectedDates.map((date) =>
            getDateFormat(date, "YYYY-MM-DD")
          ),
        }),
        start_and_end_time,
        slot_duration: +formData.slotDuration,
        buffer_between_slots: +formData.bufferDuration,
        total_slots: Number(formData.totalSlots),
        ...(formData.consultationType === IDoctorConsultationType.IN_CLINIC && {
          appointment_display: +formData?.appointmentDisplay,
          reserved_slots_count: +formData?.reserveSlots,
        }),
        ...(!isEditAvailability &&
          formData.consultationType === IDoctorConsultationType.IN_CLINIC && {
            clinic_id: formData?.clinicId?.split("_")[0],
            slot_reserve_from: +formData?.slotReserveFrom?.split("_")[0],
          }),
      };
      if (isEditAvailability) {
        createEditAvailability = await updateAvailabilitySettingApi(
          payload,
          selectedAvailabilitySettingDetail?.id!
        );
      } else {
        createEditAvailability = await createAvailabilitySettingApi(payload);
      }
      if (createEditAvailability.statusCode === IAPIStatusCode.SUCCESS) {
        dispatchApp(fetchAvailabilitySettingsListing({}));
        closeCreateOrEditAvailabilityHandler();
        notify(INotifyEnum.SUCCESS, createEditAvailability.message);
      } else {
        notify(INotifyEnum.ERROR, createEditAvailability.message);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      dispatch({
        type: ICreateEditAvailabilityAction.SET_SAVE_OR_UPDATE_LOADING,
        payload: false,
      });
      dispatchApp(setDayDateFilter(null));
    }
  };

  const createOrEditAvailabilityScreen = () => {
    return (
      <>
        {!isMobileOrTablet && (
          <Grid item sx={style.avilabilityTopContainer}>
            <Grid item sx={style.avilabilityTopContainerText}>
              <Grid
                item
                sx={style.availabilityLeftArrowIcon}
                onClick={backCreateEditAvailabilityHandler}
              >
                <GetImages name="LeftArrowIcon" width="16" height="16" />
              </Grid>
              <Typography variant="h4">
                {isEditAvailability ? (
                  getDateFormat(
                    selectedAvailabilitySettingDetail?.schedule_date,
                    "DD MMM, YYYY"
                  )
                ) : (
                  <FormattedMessage
                    id="create_new_availability"
                    defaultMessage="Create New Availability"
                  />
                )}
              </Typography>
            </Grid>
            {isEditAvailability && (
              <Grid item sx={style.addNewAvailability}>
                <Button
                  variant={IButtonVariant.WHITE}
                  btnTrigger={deleteAvailabilityHandler}
                >
                  <GetImages
                    name="TrashIcon"
                    width="30"
                    height="24"
                    fill="var(--red-600)"
                  />
                  <Typography component="p" sx={style.deleteButtonText}>
                    <FormattedMessage id="delete" defaultMessage="Delete" />
                  </Typography>
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        <Grid item sx={style.createOrEditAvailabilityContainer}>
          <Grid item display={"flex"} gap={1.6}>
            <FormControl variant="outlined" fullWidth>
              <InputField
                type={"text"}
                label={intl.formatMessage({
                  id: "typeOfConsultation",
                  defaultMessage: "Type of consultation",
                })}
                placeholder={intl.formatMessage({
                  id: "typeOfConsultation",
                  defaultMessage: "Type of consultation",
                })}
                disabled={true}
                value={consultationTypes[formData.consultationType] ?? ""}
                sx={style.disabledTextField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component={"p"}
                        sx={
                          isEditAvailability
                            ? style.notAllowed
                            : style.selectText
                        }
                        onClick={() =>
                          isEditAvailability
                            ? null
                            : dispatch({
                                type: ICreateEditAvailabilityAction.SET_CONSULTATION_TYPE_OPEN,
                                payload: true,
                              })
                        }
                      >
                        <FormattedMessage id="select" defaultMessage="Select" />
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
            {formData.consultationType ===
              IDoctorConsultationType.IN_CLINIC && (
              <FormControl variant="outlined" fullWidth>
                <InputField
                  type={"text"}
                  label={intl.formatMessage({
                    id: "clinic_name",
                    defaultMessage: "Clinic Name",
                  })}
                  placeholder={intl.formatMessage({
                    id: "clinic_name",
                    defaultMessage: "Clinic Name",
                  })}
                  disabled={true}
                  value={formData.clinicName ?? ""}
                  sx={style.disabledTextField}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          component={"p"}
                          sx={style.selectText}
                          onClick={() => {
                            const isPermissionGranted =
                              permissionRestrictionHandler(
                                IModalKeyEnums.DOCTOR,
                                IDoctorPermissionEnums.GET_DOCTOR_LINKED_CLINICS
                              );
                            if (!isPermissionGranted) return;
                            dispatch({
                              type: ICreateEditAvailabilityAction.SET_CLINIC_NAME_OPEN,
                              payload: true,
                            });
                          }}
                        >
                          <FormattedMessage
                            id="select"
                            defaultMessage="Select"
                          />
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            )}
          </Grid>
          {formData.consultationType === IDoctorConsultationType.IN_CLINIC && (
            <Grid item>
              <Typography component={"h5"} sx={style.heading}>
                Select time & Slots
              </Typography>
              <FormControl fullWidth sx={{ p: 0 }}>
                <InputField
                  type={"text"}
                  label={intl.formatMessage({
                    id: "start_end_time",
                    defaultMessage: "Start Time & End Time",
                  })}
                  placeholder={intl.formatMessage({
                    id: "start_end_time",
                    defaultMessage: "Start Time & End Time",
                  })}
                  disabled={true}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          component={"p"}
                          sx={style.selectText}
                          onClick={() => {
                            dispatch({
                              type: ICreateEditAvailabilityAction.SET_START_END_TIME,
                              payload: true,
                            });
                          }}
                        >
                          <FormattedMessage
                            id="select"
                            defaultMessage="Select"
                          />
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
              <Grid item display={"flex"} gap={1.6} marginTop={1.6}>
                {formData?.startAndEndTime?.map(
                  (date: string | IStartAndEndTime) => (
                    <Chip
                      label={
                        typeof date === "object"
                          ? `${timeInAmPmFormat(
                              date.start_time
                            )} to ${timeInAmPmFormat(date.end_time)}`
                          : ""
                      }
                      onDelete={() =>
                        typeof date === "object"
                          ? removeStartEndTimeHandler(
                              date.start_time,
                              date.end_time
                            )
                          : null
                      }
                      sx={style.disabledChip}
                    />
                  )
                )}
              </Grid>
            </Grid>
          )}
          {formData.consultationType ===
            IDoctorConsultationType.VIDEO_CONSULTATION && (
            <Grid item display={"flex"} gap={1.6}>
              <FormControl fullWidth sx={{ p: 0 }}>
                <TimePicker
                  type={ITimePickerType.START_TIME}
                  label="start_time"
                  value={formData.startTime ?? ""}
                  handleSaveTime={(value) => {
                    dispatch({
                      type: ICreateEditAvailabilityAction.SET_FORM_DATA,
                      payload: { startTime: value },
                    });
                  }}
                  disabled={true}
                />
              </FormControl>
              <FormControl fullWidth>
                <TimePicker
                  type={ITimePickerType.END_TIME}
                  label="end_time"
                  value={formData.endTime ?? ""}
                  handleSaveTime={(value) => {
                    dispatch({
                      type: ICreateEditAvailabilityAction.SET_FORM_DATA,
                      payload: { endTime: value },
                    });
                  }}
                  disabled={true}
                />
              </FormControl>
            </Grid>
          )}
          <Grid item display={"flex"} gap={1.6}>
            <FormControl fullWidth>
              <InputField
                type="text"
                value={formData.totalSlots ?? ""}
                onChange={(e) => {
                  const { value } = e.target;
                  if (!/^\d*$/.test(value)) return;
                  dispatch({
                    type: ICreateEditAvailabilityAction.SET_FORM_DATA,
                    payload: {
                      totalSlots: value,
                    },
                  });
                }}
                label={intl.formatMessage({
                  id: "total_slots",
                  defaultMessage: "Total Slots",
                })}
                variant="outlined"
                placeholder={intl.formatMessage({
                  id: "total_slots",
                  defaultMessage: "Total Slots",
                })}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputField
                type={"text"}
                label={intl.formatMessage({
                  id: "buffer_slots",
                  defaultMessage: "Buffer slots",
                })}
                placeholder={intl.formatMessage({
                  id: "buffer_slots",
                  defaultMessage: "Buffer slots",
                })}
                value={formData.bufferDuration ?? ""}
                onChange={(e) => {
                  const { value } = e.target;
                  if (!/^\d*$/.test(value)) return;
                  dispatch({
                    type: ICreateEditAvailabilityAction.SET_FORM_DATA,
                    payload: {
                      bufferDuration: value,
                    },
                  });
                }}
                name={availabilityInputsNames.bufferDuration}
              />
            </FormControl>
          </Grid>
          <Grid item display={"flex"} gap={1.6}>
            <FormControl fullWidth>
              <InputField
                value={
                  formData?.slotDuration === "Infinity"
                    ? "0"
                    : formData?.slotDuration
                }
                type={"text"}
                label={intl.formatMessage({
                  id: "slot_duration",
                  defaultMessage: "Slot Duration",
                })}
                placeholder={intl.formatMessage({
                  id: "slot_duration",
                  defaultMessage: "Slot Duration",
                })}
                name={availabilityInputsNames.slotDuration}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography component={"p"} sx={style.notAllowed}>
                        <FormattedMessage id="min" defaultMessage="min" />
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={style.disabledTextField}
                disabled
              />
            </FormControl>
            <FormControl fullWidth>
              <InputField
                value={formData.consultationFee ?? ""}
                name={availabilityInputsNames.consultationFee}
                label={`${intl.formatMessage({
                  id: "consultation_fee",
                  defaultMessage: "Consultation Fee (₹)",
                })} (₹)`}
                variant="outlined"
                placeholder={`${intl.formatMessage({
                  id: "consultation_fee",
                  defaultMessage: "Consultation Fee (₹)",
                })} (₹)`}
                onChange={(e) => {
                  const { value } = e.target;
                  if (!/^\d*$/.test(value)) return;
                  dispatch({
                    type: ICreateEditAvailabilityAction.SET_FORM_DATA,
                    payload: {
                      consultationFee: value,
                    },
                  });
                }}
                sx={style.disabledTextField}
              />
            </FormControl>
          </Grid>
          {formData.consultationType === IDoctorConsultationType.IN_CLINIC && (
            <Grid item display={"flex"} flexDirection={"column"} gap={1.6}>
              <FormControl fullWidth>
                <InputField
                  type="text"
                  value={formData.appointmentDisplay ?? ""}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (!/^\d*$/.test(value)) return;
                    dispatch({
                      type: ICreateEditAvailabilityAction.SET_FORM_DATA,
                      payload: {
                        appointmentDisplay: value,
                      },
                    });
                  }}
                  name={availabilityInputsNames.appointmentDisplay}
                  label={intl.formatMessage({
                    id: "appointment_display",
                    defaultMessage: "Appointment Display",
                  })}
                  variant="outlined"
                  placeholder={intl.formatMessage({
                    id: "appointment_display",
                    defaultMessage: "Appointment Display",
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography component={"p"} sx={style.selectText}>
                          <FormattedMessage id="min" defaultMessage="min" />
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </FormControl>
              <Grid item display={"flex"} gap={1.6}>
                <FormControl fullWidth>
                  <InputField
                    type="text"
                    value={formData.reserveSlots ?? ""}
                    onChange={(e) => {
                      const { value } = e.target;
                      if (!/^\d*$/.test(value)) return;
                      if (+value > +formData?.totalSlots) {
                        notify(
                          INotifyEnum.ERROR,
                          "Reserved slots count cannot be greater than total slots"
                        );
                      }
                      dispatch({
                        type: ICreateEditAvailabilityAction.SET_FORM_DATA,
                        payload: {
                          reserveSlots: value,
                        },
                      });
                    }}
                    name={availabilityInputsNames.reserveSlots}
                    label={"Reserve Slots by Doctor & Admin"}
                    variant="outlined"
                    placeholder={"Reserve Slots by Doctor & Admin"}
                    fullWidth
                  />
                </FormControl>
                <FormControl variant="outlined" fullWidth>
                  <InputField
                    type={"text"}
                    label={"Slots Reserve from"}
                    variant="outlined"
                    placeholder={"Slots Reserve from"}
                    disabled={true}
                    value={
                      formData?.slotReserveFrom?.split("_")[1] ??
                      slotReserveValues[formData?.slotReserveFrom]
                    }
                    sx={style.disabledTextField}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography
                            component={"p"}
                            sx={style.selectText}
                            onClick={() =>
                              dispatch({
                                type: ICreateEditAvailabilityAction.SET_SLOT_RESERVE_FROM,
                                payload: true,
                              })
                            }
                          >
                            <FormattedMessage
                              id="select"
                              defaultMessage="Select"
                            />
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}
          <Grid item>
            <Typography component={"h5"} sx={style.heading}>
              Choose Dates
            </Typography>
            <FormControl fullWidth>
              <DatePickerMultipleSelection
                selectedDates={selectedDates}
                setSelectedDates={handleDateSelection}
                disabled={isEditAvailability}
              />
            </FormControl>
          </Grid>
          {selectedDates.length > 0 && (
            <Grid item>
              <Typography component={"h5"} sx={style.selectedDate}>
                Selected Date
              </Typography>
              <Grid item display={"flex"} gap={1.6} mt={0.8} flexWrap={"wrap"}>
                {selectedDates.map((date) => (
                  <Chip
                    label={getDateFormat(date, "ddd, DD MMM YYYY")}
                    onDelete={() => {
                      dispatch({
                        type: ICreateEditAvailabilityAction.SET_SELECTED_DATES,
                        payload: selectedDates.filter((d) => d !== date),
                      });
                    }}
                    disabled={isEditAvailability}
                    sx={style.disabledChip}
                  />
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item sx={style.saveCancelBtnContainer}>
          <Button
            variant={IButtonVariant.WHITE}
            btnTrigger={closeCreateOrEditAvailabilityHandler}
          >
            <FormattedMessage id={"cancel"} defaultMessage={"Cancel"} />
          </Button>
          <Button
            isLoading={saveOrUpdateLoading}
            btnTrigger={createOrEditAvailabilitySettingsHandler}
          >
            <FormattedMessage
              id={isEditAvailability ? "update" : "save"}
              defaultMessage={isEditAvailability ? "Update" : "Save"}
            />
          </Button>
        </Grid>
        <Grid item sx={style.saveCancelBtnContainerDesktop}>
          <SaveCancelModalFooterActions
            isLoading={saveOrUpdateLoading}
            saveLabel={`${isEditAvailability ? "Update" : "Save"}`}
            saveHandler={createOrEditAvailabilitySettingsHandler}
            cancelHandler={closeCreateOrEditAvailabilityHandler}
          />
        </Grid>
      </>
    );
  };

  useEffect(() => {
    if (
      ((formData.startTime && formData.endTime) ||
        formData.startAndEndTime.length > 0) &&
      formData.totalSlots &&
      formData.bufferDuration
    ) {
      const slotDuration = calculateSlotDuration(
        +formData.totalSlots,
        +formData.bufferDuration,
        formData.consultationType === IDoctorConsultationType.IN_CLINIC
          ? formData.startAndEndTime
          : [{ start_time: formData.startTime, end_time: formData.endTime }]
      );
      if (!slotDuration) return;
      dispatch({
        type: ICreateEditAvailabilityAction.SET_FORM_DATA,
        payload: {
          slotDuration: slotDuration < 0 ? "0" : slotDuration.toString(),
        },
      });
    }
  }, [
    formData.startTime,
    formData.endTime,
    formData.totalSlots,
    formData.bufferDuration,
    formData.startAndEndTime,
  ]);

  useEffect(() => {
    if (isEditAvailability && selectedAvailabilitySettingDetail) {
      dispatch({
        type: ICreateEditAvailabilityAction.SET_FORM_DATA,
        payload: {
          consultationType:
            selectedAvailabilitySettingDetail.type_of_consultation,
          clinicId: selectedAvailabilitySettingDetail.clinic_id,
          clinicName: selectedAvailabilitySettingDetail.clinic_name,
          startTime:
            selectedAvailabilitySettingDetail.start_and_end_time[0].start_time,
          endTime:
            selectedAvailabilitySettingDetail.start_and_end_time[0].end_time,
          totalSlots: selectedAvailabilitySettingDetail.total_slots.toString(),
          bufferDuration:
            selectedAvailabilitySettingDetail.buffer_between_slots.toString(),
          slotDuration:
            selectedAvailabilitySettingDetail?.slot_duration?.toString(),
          consultationFee:
            selectedAvailabilitySettingDetail.consultation_fee.toString(),
          appointmentDisplay:
            selectedAvailabilitySettingDetail?.appointment_display?.toString() ??
            "",
          reserveSlots:
            selectedAvailabilitySettingDetail?.reserved_slots_count?.toString() ??
            "",
          startAndEndTime:
            selectedAvailabilitySettingDetail?.start_and_end_time ?? [],
          slotReserveFrom:
            selectedAvailabilitySettingDetail?.slot_reserve_from.toString(),
        },
      });
      dispatch({
        type: ICreateEditAvailabilityAction.SET_SELECTED_DATES,
        payload: [
          selectedAvailabilitySettingDetail.schedule_date.split("T")[0],
        ],
      });
    }
  }, [selectedAvailabilitySettingDetail]);
  return (
    <>
      {isMobileOrTablet ? (
        <FullScreenDialog>
          <Grid container sx={style.headerContainer}>
            <Grid item sx={style.headerInnerContainer}>
              <GetImages name="SettingGreyedIcon" width="24" height="24" />
              <Grid item ml={0.4}>
                <Typography component={"p"} sx={style.headerText}>
                  Availability Settings
                </Typography>
              </Grid>
            </Grid>
            <Grid item display="flex" alignItems="center">
              {isEditAvailability && (
                <Grid
                  item
                  mr={1.6}
                  onClick={() => dispatchApp(setIsDeleteAvailability(true))}
                >
                  <GetImages
                    name="TrashIcon"
                    width="24"
                    height="24"
                    fill="var(--red-600)"
                  />
                </Grid>
              )}
              <Grid item onClick={closeCreateOrEditAvailabilityHandler}>
                <CloseIcon sx={style.closeIcon} />
              </Grid>
            </Grid>
          </Grid>
          {createOrEditAvailabilityScreen()}
        </FullScreenDialog>
      ) : (
        createOrEditAvailabilityScreen()
      )}
      <CreateEditAvailabilityOverlays
        formData={formData}
        slotReserveFromOpen={slotReserveFromOpen}
        consultationTypeOrClinicSelectionOpen={
          consultationTypeOrClinicSelectionOpen
        }
        startEndSaveTimeHandler={startEndSaveTimeHandler}
        startEndTimeSelectionOpen={startEndTimeSelectionOpen}
        closeAvailabilitySelectionModal={closeAvailabilitySelectionModal}
        selectTypeOfConsultationHandler={selectTypeOfConsultationHandler}
        deleteAvailabilitySetting={deleteAvailabilitySetting}
        saveClinicNameHandler={saveClinicNameHandler}
        saveSlotReserveFromHandler={saveSlotReserveFromHandler}
        closeSlotReserveFromHandler={closeSlotReserveFromHandler}
        closeStartEndTimePickerHandler={closeStartEndTimePickerHandler}
      />
    </>
  );
};
export default CreateEditAvailability;
